import { createReducer, isAnyOf } from '@reduxjs/toolkit';
import {
  ASSIGN_PAYBOX,
  ASSIGN_PAYREGI,
  ASSIGN_PAYSPOT,
  CLEAR_KIOSK_DETAIL,
  CLEAR_SELECTED_PAYBOX_IDS,
  CLEAR_SELECTED_PAYREGI_IDS,
  CLEAR_SELECTED_PAYSPOT_IDS,
  CREATE_POKEREGI,
  DELETE_PAYBOX,
  DELETE_PAYREGI,
  DELETE_PAYSPOT,
  GET_KIOSKS,
  GET_KIOSK_CASH_COLLECTIONS,
  GET_KIOSK_DETAIL,
  GET_KIOSK_HEALTHS,
  GET_KIOSK_MAINTENANCES,
  GET_KIOSK_PLAYS,
  GET_KIOSK_PLAY_DETAIL,
  GET_PAYBOXES,
  GET_PAYBOX_STOCKS,
  GET_PAYREGIS,
  GET_PAYREGI_STOCKS,
  GET_PAYSPOTS,
  GET_PAYSPOT_STOCKS,
  GET_POKEREGIS,
  INVALIDATE_POKEREGI,
  SET_LAST_DEVICE_PAGE_PATH,
  SET_SELECTED_PAYBOX_IDS,
  SET_SELECTED_PAYREGI_IDS,
  SET_SELECTED_PAYSPOT_IDS,
  SHOW_PAYBOX,
  SHOW_PAYREGI,
  SHOW_PAYSPOT,
  SHOW_POKEREGI,
  UPDATE_POKEREGI,
} from '../actions/devices';

type Paginated<T> = {
  rows: T[];
  count: number;
  pagination: {
    current: number;
    per_page: number;
  };
};

// TODO: infer types from docs
type Pokeregi = unknown;
export type Payregi = {
  id: string;
  serial_number: string;
  pcb_ver: string;
  money_name: string;
  topup_enabled: boolean;
};
type PayregiStock = unknown;
export type Paybox = {
  id: string;
  serial_number: string;
  hardware_ver: string;
  money_name: string;
  topup_enabled: boolean;
};
type PayboxStock = unknown;
export type Payspot = {
  id: string;
  serial_number: string;
  hardware_ver: string;
  money_name: string;
  topup_enabled: boolean;
};
type PayspotStock = unknown;
type Kiosk = {
  latest_health: unknown;
};
type KioskPlayDetail = unknown;
type KioskMaintenance = unknown;
type KioskHealth = unknown;
type KioskPlay = unknown;
type KioskCashCollection = unknown;

export type DevicesState = {
  pokeregis: Paginated<Pokeregi>;
  payregis: Paginated<Payregi>;
  payregiStocks: Paginated<PayregiStock>;
  payboxes: Paginated<Paybox>;
  payboxStocks: Paginated<PayboxStock>;
  payspots: Paginated<Payspot>;
  payspotStocks: Paginated<PayspotStock>;
  kiosks: Paginated<Kiosk>;
  pokeregi: Pokeregi;
  payregi: Payregi;
  paybox: Paybox;
  payspot: Payspot;
  kiosk: Kiosk;
  kioskPlayDetail: KioskPlayDetail;
  kioskMaintenances: Paginated<KioskMaintenance>;
  kioskHealths: Paginated<KioskHealth>;
  kioskPlays: Paginated<KioskPlay>;
  kioskCashCollections: Paginated<KioskCashCollection>;
  selectedPayregiIds: number[];
  selectedPayspotIds: number[];
  selectedPayboxIds: number[];
  lastDevicePagePath: string | null;
};

const initState: DevicesState = {
  pokeregis: { rows: [], count: 0, pagination: { current: 1, per_page: 50 } },
  payregis: { rows: [], count: 0, pagination: { current: 1, per_page: 50 } },
  payregiStocks: { rows: [], count: 0, pagination: { current: 1, per_page: 50 } },
  payboxes: { rows: [], count: 0, pagination: { current: 1, per_page: 50 } },
  payboxStocks: { rows: [], count: 0, pagination: { current: 1, per_page: 50 } },
  payspots: { rows: [], count: 0, pagination: { current: 1, per_page: 50 } },
  payspotStocks: { rows: [], count: 0, pagination: { current: 1, per_page: 50 } },
  kiosks: { rows: [], count: 0, pagination: { current: 1, per_page: 50 } },
  pokeregi: {},
  payregi: {},
  paybox: {},
  payspot: {},
  kiosk: { latest_health: {} },
  kioskPlayDetail: {},
  kioskMaintenances: { rows: [], count: 0, pagination: { current: 1, per_page: 50 } },
  kioskHealths: { rows: [], count: 0, pagination: { current: 1, per_page: 50 } },
  kioskPlays: { rows: [], count: 0, pagination: { current: 1, per_page: 50 } },
  kioskCashCollections: { rows: [], count: 0, pagination: { current: 1, per_page: 50 } },
  selectedPayregiIds: [],
  selectedPayspotIds: [],
  selectedPayboxIds: [],
  lastDevicePagePath: null,
};

const devicesReducer = createReducer(initState, (builder) =>
  builder
    .addCase(GET_POKEREGIS, (state, action) => {
      return { ...state, pokeregis: action.payload };
    })
    .addCase(GET_PAYREGIS, (state, action) => {
      return { ...state, payregis: action.payload };
    })
    .addCase(GET_PAYREGI_STOCKS, (state, action) => {
      return { ...state, payregiStocks: action.payload };
    })
    .addCase(GET_PAYBOXES, (state, action) => {
      return { ...state, payboxes: action.payload };
    })
    .addCase(GET_PAYBOX_STOCKS, (state, action) => {
      return { ...state, payboxStocks: action.payload };
    })
    .addCase(GET_PAYSPOTS, (state, action) => {
      return { ...state, payspots: action.payload };
    })
    .addCase(GET_PAYSPOT_STOCKS, (state, action) => {
      return { ...state, payspotStocks: action.payload };
    })
    .addCase(GET_KIOSKS, (state, action) => {
      return { ...state, kiosks: action.payload };
    })
    .addCase(GET_KIOSK_DETAIL, (state, action) => {
      return { ...state, kiosk: action.payload };
    })
    .addCase(CLEAR_KIOSK_DETAIL, (state) => {
      return { ...state, kiosk: initState.kiosk };
    })
    .addCase(GET_KIOSK_MAINTENANCES, (state, action) => {
      return { ...state, kioskMaintenances: action.payload };
    })
    .addCase(GET_KIOSK_HEALTHS, (state, action) => {
      return { ...state, kioskHealths: action.payload };
    })
    .addCase(GET_KIOSK_PLAYS, (state, action) => {
      return { ...state, kioskPlays: action.payload };
    })
    .addCase(GET_KIOSK_CASH_COLLECTIONS, (state, action) => {
      return { ...state, kioskCashCollections: action.payload };
    })
    .addCase(GET_KIOSK_PLAY_DETAIL, (state, action) => {
      return { ...state, kioskPlayDetail: action.payload };
    })
    .addCase(SET_SELECTED_PAYREGI_IDS, (state, action) => {
      return { ...state, selectedPayregiIds: action.payload };
    })
    .addCase(CLEAR_SELECTED_PAYREGI_IDS, (state) => {
      return { ...state, selectedPayregiIds: [] };
    })
    .addCase(SET_SELECTED_PAYSPOT_IDS, (state, action) => {
      return { ...state, selectedPayspotIds: action.payload };
    })
    .addCase(CLEAR_SELECTED_PAYSPOT_IDS, (state) => {
      return { ...state, selectedPayspotIds: [] };
    })
    .addCase(SET_SELECTED_PAYBOX_IDS, (state, action) => {
      return { ...state, selectedPayboxIds: action.payload };
    })
    .addCase(CLEAR_SELECTED_PAYBOX_IDS, (state) => {
      return { ...state, selectedPayboxIds: [] };
    })
    .addCase(SET_LAST_DEVICE_PAGE_PATH, (state, action) => {
      return { ...state, lastDevicePagePath: action.payload };
    })
    .addMatcher(isAnyOf(SHOW_PAYREGI, ASSIGN_PAYREGI, DELETE_PAYREGI), (state, action) => {
      return { ...state, payregi: action.payload };
    })
    .addMatcher(isAnyOf(SHOW_PAYBOX, ASSIGN_PAYBOX, DELETE_PAYBOX), (state, action) => {
      return { ...state, paybox: action.payload };
    })
    .addMatcher(isAnyOf(SHOW_PAYSPOT, ASSIGN_PAYSPOT, DELETE_PAYSPOT), (state, action) => {
      return { ...state, payspot: action.payload };
    })
    .addMatcher(
      isAnyOf(CREATE_POKEREGI, UPDATE_POKEREGI, SHOW_POKEREGI, INVALIDATE_POKEREGI),
      (state, action) => {
        return { ...state, pokeregi: action.payload };
      }
    )
);

export default devicesReducer;
